export const KEYS = {
  TIME: 'time',
  PUZZLE_SIGNATURE: 'puzzle.signature',
  PUZZLE_LASTWON: 'puzzle.lastwon',
  HAS_OPENED_TODAYS_BOOSTER_PACK: 'puzzle.booster.opened',
  LASTWON_CARD: 'poketta.lastwon.card',
  DECK: 'poketta.deck',
  PLACED_CARDS: 'placedCards',
  HAS_INTERACTED: 'hasInteracted',
  IS_HARD_MODE: 'isHardMode',
  IS_DARK_MODE: 'isDarkMode',
  SHOW_VARIANT_CARDS: 'showVariantCards',
  USE_CLASSICAL_CARDS: 'useClassicalCards',
  GIFT_CARDS: 'poketta.giftcards',
  GIFT_CARD_CLAIMS: 'poketta.giftclaims',
  SECRET_DECKS_OPENED: 'poketta.decks.opened',
};

// Debug-friendly single place to tune gift card lifetime.
export const GIFT_CARD_DURATION_MS = 30 * 1000;
export const GIFT_CARD_DURATION_SECONDS = Math.floor(GIFT_CARD_DURATION_MS / 1000);

export const getDeck = () => {
  try { return JSON.parse(localStorage.getItem(KEYS.DECK) || '{}'); }
  catch { return {}; }
};

export const getDeckStats = () => {
  const deck = getDeck();
  const unique = Object.keys(deck).length;
  const total = Object.values(deck).reduce((a, b) => a + b, 0);
  return { unique, total };
};

// Gift Card utilities
const encodeGiftToken = (payload) => {
  const json = JSON.stringify(payload);
  const base64 = btoa(json);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const decodeGiftToken = (token) => {
  try {
    const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const parseGiftCardToken = (token) => {
  const payload = decodeGiftToken(token);
  if (!payload || typeof payload !== 'object') return null;
  if (!payload.id || !payload.card || !payload.expiresAt) return null;
  return payload;
};

const getGiftCardClaims = () => {
  try { return JSON.parse(localStorage.getItem(KEYS.GIFT_CARD_CLAIMS) || '{}'); }
  catch { return {}; }
};

const pruneExpiredGiftCardClaims = (claims) => {
  const now = Date.now();
  const nextClaims = {};

  Object.entries(claims).forEach(([id, value]) => {
    if (typeof value === 'number') {
      // Backward compatibility for old shape: { [id]: claimedAt }.
      // Keep for one full gift duration after claim, then prune.
      if ((value + GIFT_CARD_DURATION_MS) > now) {
        nextClaims[id] = value;
      }
      return;
    }

    if (value && typeof value === 'object' && typeof value.expiresAt === 'number') {
      if (value.expiresAt > now) {
        nextClaims[id] = value;
      }
      return;
    }

    // Unknown shape; drop it to avoid unbounded growth/corruption.
  });

  return nextClaims;
};

export const createGiftCard = (card) => {
  // Generate a unique ID for this gift card
  const createdAt = Date.now();
  const giftCardId = 'gc_' + Math.random().toString(36).substr(2, 9) + '_' + createdAt;
  const expiresAt = createdAt + GIFT_CARD_DURATION_MS;
  
  const giftCards = getGiftCards();
  giftCards[giftCardId] = {
    card,
    createdAt,
    expiresAt,
    consumed: false,
  };
  
  localStorage.setItem(KEYS.GIFT_CARDS, JSON.stringify(giftCards));

  // Token is portable across devices without requiring shared localStorage.
  return encodeGiftToken({
    id: giftCardId,
    card,
    createdAt,
    expiresAt,
  });
};

export const getGiftCards = () => {
  try { return JSON.parse(localStorage.getItem(KEYS.GIFT_CARDS) || '{}'); }
  catch { return {}; }
};

export const getGiftCard = (giftCardId) => {
  const giftCards = getGiftCards();
  return giftCards[giftCardId] || null;
};

export const consumeGiftCard = (giftCardId) => {
  const giftCard = getGiftCard(giftCardId);
  
  if (!giftCard) return false;
  if (giftCard.consumed) return false;
  if (Date.now() > giftCard.expiresAt) return false;
  
  // Mark as consumed and add card to deck
  const giftCards = getGiftCards();
  giftCards[giftCardId].consumed = true;
  localStorage.setItem(KEYS.GIFT_CARDS, JSON.stringify(giftCards));
  
  // Add card to player's deck
  const deck = getDeck();
  deck[giftCard.card] = (deck[giftCard.card] || 0) + 1;
  localStorage.setItem(KEYS.DECK, JSON.stringify(deck));
  
  return true;
};

export const consumeGiftCardFromToken = (token) => {
  const payload = parseGiftCardToken(token);
  if (!payload) return false;

  if (Date.now() > payload.expiresAt) return false;

  const claims = pruneExpiredGiftCardClaims(getGiftCardClaims());
  if (claims[payload.id]) {
    localStorage.setItem(KEYS.GIFT_CARD_CLAIMS, JSON.stringify(claims));
    return false;
  }

  claims[payload.id] = {
    claimedAt: Date.now(),
    expiresAt: payload.expiresAt,
  };
  localStorage.setItem(KEYS.GIFT_CARD_CLAIMS, JSON.stringify(claims));

  const deck = getDeck();
  deck[payload.card] = (deck[payload.card] || 0) + 1;
  localStorage.setItem(KEYS.DECK, JSON.stringify(deck));

  return true;
};
